"use client";

import { deleteCookie, getCookie, setCookie } from "cookies-next/client";
import { env } from "@/env";
import {
  CONSENT_COOKIE_NAME,
  CONSENT_DURATION_SECONDS,
  type ConsentValue,
  readConsentChoice,
} from "./consent";
import { getFreshAccessToken } from "./nhost/access-token";

const legacyChoice = "t_ads_consent";
const legacyPending = "t_ads_consent_pending";

export function getConsentChoice(): ConsentValue | undefined {
  return readConsentChoice(getCookie(CONSENT_COOKIE_NAME));
}

function writeChoice(choice: ConsentValue, pending: boolean) {
  setCookie(CONSENT_COOKIE_NAME, `${choice}.v2.${pending ? "pending" : "saved"}`, {
    maxAge: CONSENT_DURATION_SECONDS,
    path: "/",
    sameSite: "lax",
    secure: window.location.protocol === "https:",
  });
}

export function saveConsentChoice(choice: ConsentValue) {
  writeChoice(choice, true);
  deleteCookie(legacyChoice, { path: "/" });
  deleteCookie(legacyPending, { path: "/" });
}

let pending: Promise<void> = Promise.resolve();

export function syncMeasurementConsent(): Promise<void> {
  // Serialize choices so a slow grant cannot overwrite a later withdrawal.
  pending = pending
    .catch(() => {})
    .then(async () => {
      const raw = getCookie(CONSENT_COOKIE_NAME);
      const choice = readConsentChoice(raw);
      if (!choice || raw === `${choice}.v2.saved`) return;
      // An existing browser choice can initialize an account, never undo its withdrawal.
      const onlyIfUnset = raw === choice && getCookie(legacyPending) !== choice;
      const token = await getFreshAccessToken();
      if (!token) return;
      const response = await fetch(`${env.NEXT_PUBLIC_API_URL}/ads/consent`, {
        method: "POST",
        headers: { "content-type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ consent: choice, ...(onlyIfUnset ? { onlyIfUnset: true } : {}) }),
        signal: AbortSignal.timeout(5000),
      });
      if (!response.ok) throw new Error("Could not save account measurement preference");
      if (getCookie(CONSENT_COOKIE_NAME) === raw) {
        writeChoice(choice, false);
        deleteCookie(legacyChoice, { path: "/" });
        deleteCookie(legacyPending, { path: "/" });
      }
    });
  return pending;
}
